import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, User, LogOut, Settings, Calendar, Play, FileText, HelpCircle, Mail, Info } from 'lucide-react'
import useAuthStore from '../stores/authStore'
import { getUserPlays } from '../services/playsApi'
import { getUserPracticeSessions } from '../services/practiceSessionsApi'

const HomePage = () => {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const [plays, setPlays] = useState([])
  const [sessions, setSessions] = useState([])
  const [isLoadingPlays, setIsLoadingPlays] = useState(true)
  const [isLoadingSessions, setIsLoadingSessions] = useState(true)
  const [manageDropdown, setManageDropdown] = useState(false)
  const [planDropdown, setPlanDropdown] = useState(false)
  const [designDropdown, setDesignDropdown] = useState(false)
  const [userDropdown, setUserDropdown] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setIsLoadingPlays(true)
      setIsLoadingSessions(true)
      const [playsData, sessionsData] = await Promise.all([
        getUserPlays().catch(() => []),
        getUserPracticeSessions().catch(() => [])
      ])
      setPlays(playsData)
      setSessions(sessionsData)
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setIsLoadingPlays(false)
      setIsLoadingSessions(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const handlePlayClick = (playId) => {
    navigate(`/soccer-designer?load=${playId}`)
  }

  const handleSessionClick = (sessionId) => {
    navigate(`/practice-planner?load=${sessionId}`)
  }

  const handleComingSoon = (e) => {
    e.preventDefault()
    alert('Coming soon!')
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="w-full px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img 
                src="/sportigee-blue-no-background.png" 
                alt="Sportigee" 
                className="h-12 w-auto"
              />
            </div>

            {/* Navigation Dropdowns */}
            <nav className="flex items-center space-x-4">
              {/* Manage Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setManageDropdown(!manageDropdown)
                    setPlanDropdown(false)
                    setDesignDropdown(false)
                    setUserDropdown(false)
                  }}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>Manage</span>
                  <ChevronDown size={16} />
                </button>
                {manageDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button onClick={handleComingSoon} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      My Account
                    </button>
                    <button onClick={handleComingSoon} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      My Organization
                    </button>
                    <button onClick={handleComingSoon} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      My Teams
                    </button>
                  </div>
                )}
              </div>

              {/* Plan Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setPlanDropdown(!planDropdown)
                    setManageDropdown(false)
                    setDesignDropdown(false)
                    setUserDropdown(false)
                  }}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>Plan</span>
                  <ChevronDown size={16} />
                </button>
                {planDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button
                      onClick={() => {
                        navigate('/practice-planner')
                        setPlanDropdown(false)
                      }}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Calendar size={16} />
                      <span>Practice Planner</span>
                    </button>
                  </div>
                )}
              </div>

              {/* Design Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setDesignDropdown(!designDropdown)
                    setManageDropdown(false)
                    setPlanDropdown(false)
                    setUserDropdown(false)
                  }}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <span>Design</span>
                  <ChevronDown size={16} />
                </button>
                {designDropdown && (
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button
                      onClick={() => {
                        navigate('/soccer-designer')
                        setDesignDropdown(false)
                      }}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <Play size={16} />
                      <span>Play Designer</span>
                    </button>
                  </div>
                )}
              </div>

              {/* User Profile Dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    setUserDropdown(!userDropdown)
                    setManageDropdown(false)
                    setPlanDropdown(false)
                    setDesignDropdown(false)
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <User size={18} />
                  <span className="hidden sm:inline">{user?.username || 'User'}</span>
                  <ChevronDown size={16} />
                </button>
                {userDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 border border-gray-200 z-50">
                    <button onClick={handleComingSoon} className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <Settings size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button onClick={handleComingSoon} className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <HelpCircle size={16} />
                      <span>Help & Support</span>
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      <LogOut size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img 
              src="/sportigee-blue-no-background.png" 
              alt="Sportigee" 
              className="h-24 w-auto"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Welcome to Sportigee</h1>
          <p className="text-gray-600 mt-2">Manage your practice sessions and plays</p>
        </div>

        {/* Tables Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Practice Sessions Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                  <Calendar size={20} />
                  <span>My Sessions</span>
                </h2>
                <button
                  onClick={() => navigate('/practice-planner')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Plan a Session →
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoadingSessions ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : sessions.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                        No practice sessions yet. <button onClick={() => navigate('/practice-planner')} className="text-blue-600 hover:underline">Create one</button>
                      </td>
                    </tr>
                  ) : (
                    sessions.map((session) => (
                      <tr
                        key={session.id}
                        onClick={() => handleSessionClick(session.id)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{session.name}</div>
                          {session.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {session.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(session.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                            {session.sportType || 'soccer'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Plays Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
                  <Play size={20} />
                  <span>My Plays</span>
                </h2>
                <button
                  onClick={() => navigate('/soccer-designer')}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Design a Play →
                </button>
              </div>
            </div>
            <div className="overflow-auto max-h-96">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {isLoadingPlays ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                        Loading...
                      </td>
                    </tr>
                  ) : plays.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                        No plays yet. <button onClick={() => navigate('/soccer-designer')} className="text-blue-600 hover:underline">Create one</button>
                      </td>
                    </tr>
                  ) : (
                    plays.map((play) => (
                      <tr
                        key={play.id}
                        onClick={() => handlePlayClick(play.id)}
                        className="hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{play.name}</div>
                          {play.description && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">
                              {play.description}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(play.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 capitalize">
                            {play.sportType || 'soccer'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">About</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Our Story</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Mission</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Team</button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Support</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Help Center</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Documentation</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">FAQs</button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Contact</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Contact Us</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Feedback</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Report Issue</button>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Legal</h3>
              <ul className="space-y-2">
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Privacy Policy</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Terms of Service</button>
                </li>
                <li>
                  <button onClick={handleComingSoon} className="text-sm text-gray-600 hover:text-gray-900">Cookie Policy</button>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Sportigee. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Click outside to close dropdowns */}
      {(manageDropdown || planDropdown || designDropdown || userDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setManageDropdown(false)
            setPlanDropdown(false)
            setDesignDropdown(false)
            setUserDropdown(false)
          }}
        />
      )}
    </div>
  )
}

export default HomePage

